import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { usePerson } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";

import type { EssentialModule } from "@/shared/types/estateEssentials";

import { buildTaskAnswers } from "../utils/taskAnswers";

export function useSaveTaskAnswers(module: EssentialModule | undefined, isVehicleModule: boolean) {
  const person = usePerson();
  const queryClient = useQueryClient();

  return useCallback(
    async (data: Record<string, string>): Promise<string | null> => {
      if (!module?.taskId || !person?.estate_id) return null;

      const answers = buildTaskAnswers(module, data, isVehicleModule);
      const now = new Date().toISOString();
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          task_id: module.taskId,
          estate_id: person.estate_id,
          status: "in_progress",
          answers,
          started_at: now,
        },
        { onConflict: "estate_id,task_id" },
      );

      await queryClient.invalidateQueries({
        queryKey: ["estateTaskAnswers", person.estate_id, module.taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["moduleProgress", person.estate_id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["elayneTasks", person.estate_id],
      });

      if (error) {
        console.error("Failed to save estate task:", error);
        return "Failed to save your answers. Please try again.";
      }
      return null;
    },
    [module, isVehicleModule, person?.estate_id, queryClient],
  );
}
